import React, { useRef, useState } from "react";
import { Upload, Download, FileText, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";

export default function BulkImportProductsButton({ onImported }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  function reset() {
    setFile(null);
    setResult(null);
    setError(null);
    if (fileRef.current) fileRef.current.value = "";
  }

  function closeAll() {
    setOpen(false);
    reset();
  }

  function downloadErrorReport() {
    if (!result?.errors?.length) return;
    const lines = ["Row,Reason"];
    for (const e of result.errors) {
      const reason = String(e.reason || "").replace(/"/g, '""');
      lines.push(`${e.row},"${reason}"`);
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "products-import-errors.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function upload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/bulk-import", fd);
      setResult(data);
      if (data.created > 0 && onImported) onImported();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="bulk-import-products-btn">
          <Upload className="mr-1 h-3.5 w-3.5" /> Bulk Import
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Import Products</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple products at once. Invalid rows will be skipped and reported.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">CSV file</label>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setError(null); }}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200"
                data-testid="bulk-import-file-input"
              />
              {file && (
                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                  <FileText className="h-3.5 w-3.5" /> {file.name} · {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="bulk-import-error">
                {error}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeAll} disabled={uploading}>Cancel</Button>
              <Button onClick={upload} disabled={!file || uploading} data-testid="bulk-import-upload-btn">
                {uploading && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                Upload & Import
              </Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-4" data-testid="bulk-import-result">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-center">
                <p className="text-xs uppercase tracking-wide text-slate-500">Total rows</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{result.total}</p>
              </div>
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-center">
                <p className="flex items-center justify-center gap-1 text-xs uppercase tracking-wide text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Created
                </p>
                <p className="mt-1 text-2xl font-bold text-emerald-700" data-testid="bulk-import-created-count">{result.created}</p>
              </div>
              <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-center">
                <p className="flex items-center justify-center gap-1 text-xs uppercase tracking-wide text-rose-700">
                  <AlertCircle className="h-3.5 w-3.5" /> Errors
                </p>
                <p className="mt-1 text-2xl font-bold text-rose-700" data-testid="bulk-import-error-count">{result.errors?.length || 0}</p>
              </div>
            </div>

            {result.errors?.length > 0 && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium">Row-level errors</p>
                  <Button variant="ghost" size="sm" onClick={downloadErrorReport} data-testid="download-errors-btn">
                    <Download className="mr-1 h-3.5 w-3.5" /> Download errors CSV
                  </Button>
                </div>
                <div className="max-h-60 overflow-y-auto rounded-md border border-rose-200 bg-white">
                  <table className="w-full text-xs">
                    <thead className="border-b bg-rose-50 text-left text-rose-700">
                      <tr>
                        <th className="px-3 py-2 font-medium">Row</th>
                        <th className="px-3 py-2 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((e, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="px-3 py-2 font-mono text-slate-700">{e.row}</td>
                          <td className="px-3 py-2 text-slate-800">{e.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={reset}>Import another file</Button>
              <Button onClick={closeAll} data-testid="bulk-import-done-btn">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
